import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import bowser from 'yola-bowser';
import { HSLColor } from '@yola/yola-palette-generator';
import { customization, dialogs, i18next, view, hdrm } from '@yola/ws-sdk';
import segment from 'src/js/modules/analytics/segment';
import { SURFACES_ATTRIBUTE } from '../../blocks/constants/display-options';
import { BLOCK_SETTINGS_APPEARANCE_TAB_ID } from '../../blocks/constants/common';
import isOnlineStoreBlock from '../../onlinestore/verifiers/is-online-store-block';
import dialogTypes from '../../dialogs/constants/dialog-types';
import useSharedDataSnapshot from '../../dialogs/hooks/use-shared-data-snapshot';
import useDialogPosition from '../../dialogs/hooks/use-dialog-position';
import ColorSettingsDialog from '../components/color-settings-dialog';
import actions from '../actions';
import appendCustomSchemeToPaletteElement from '../helpers/append-custom-scheme-to-palette-element';
import getGradientTraitTypeByGradientModelType from '../helpers/get-gradient-trait-type-by-gradient-model-type';
import parseHslColorString from '../helpers/parse-hsl-color-string';
import isLightColorType from '../helpers/is-light-color-type';
import getCustomSchemeLabel from '../helpers/get-custom-scheme-label';
import createPaletteEngine from '../helpers/create-palette-engine';
import generateCustomSchemeId from '../helpers/generate-custom-scheme-id';
import updatePalettesWith from '../helpers/update-palettes-with';
import updateCustomPalettes from '../helpers/update-custom-palettes';
import saveGlobalPalette from '../helpers/save-global-palette';
import buildGradientModelByString from '../helpers/build-gradient-model-by-string';
import { buildSolidColorFromGradientCSSValue } from '../helpers/build-solid-color-from-gradient';
import stringifyGradientModel from '../helpers/stringify-gradient-model';
import updateActiveColorStopInGradientModel from '../helpers/update-active-color-stop-in-gradient-model';
import deriveGradientBaseColorFromGradientModel from '../helpers/derive-gradient-base-color-from-gradient-model';
import { buildBlockBackgroundLayerOverrides } from '../helpers/get-advanced-colors-to-override';
import useColorModeState from '../hooks/use-color-mode-state';
import usePredefinedGradients from '../hooks/use-predefined-gradients';
import DEFAULT_COLOR_SCHEMES from '../constants/color-schemes';
import constants from '../constants';
import supportedColorModes from '../constants/supported-color-modes';
import variableNames, { variableVariants } from '../constants/variable-names';
import { LIGHT_COLOR_TYPE, DARK_COLOR_TYPE } from '../constants/color-types';
import gradientBaseColorTypes from '../constants/gradient-base-color-types';
import triggerIds from '../constants/trigger-ids';

const {
  trackers: { trackEvent },
  constants: { events },
} = segment;

const { updateStrategies } = hdrm.constants;

const GRADIENT_CHANGE_REASONS = {
  CHANGE_LINEAR_ANGLE: 'change-linear-angle',
  CHANGE_RADIAL_POSITION: 'change-radial-position',
  CHANGE_STOP_POSITION: 'change-stop-position',
  ADD_STOP: 'add-stop',
  FLIP_STOPS: 'flip-stops',
  DELETE_STOP: 'delete-stop',
};

const getElementSurfaces = (element, attribute) => {
  const value = element.getAttribute(attribute) || '';
  return value ? value.split(' ') : [''];
};

const getInitialColor = ({
  targetElementIds,
  originSurfacesById,
  surfaceIndex,
  schemes,
  colors,
}) => {
  const firstElementId = targetElementIds[0];
  if (!firstElementId) return Object.values(colors)[0];

  const originSurface = originSurfacesById[firstElementId][surfaceIndex];
  return schemes?.[originSurface]?.baseColor || colors[originSurface] || Object.values(colors)[0];
};

const getInitialGradientCSSValue = ({
  targetElementIds,
  originSurfacesById,
  surfaceIndex,
  schemes,
}) => {
  const firstElementId = targetElementIds[0];

  if (!firstElementId) return null;

  const originSurface = originSurfacesById[firstElementId][surfaceIndex];

  return (
    schemes?.[originSurface]?.variableOverrides?.[
      variableVariants[variableNames.BLOCK_BACKGROUND].GRADIENT
    ] || null
  );
};

const getElementAttrs = (elementId) =>
  Array.from(view.accessors.getLiveElement(elementId).attributes).reduce((acc, { name, value }) => {
    acc[name] = value;
    return acc;
  }, {});

const getCaptions = () => ({
  title: i18next.t('Color settings'),
  apply: i18next.t('Submit'),
  cancel: i18next.t('Cancel'),
  gradientPicker: {
    colorStopControlTitle: i18next.t('Stop color'),
    colorStopControlPopoverContent: i18next.t(
      'Color at this gradient stop. Move it along the slider to adjust the blend.'
    ),
    linearOptionLabel: i18next.t('Linear'),
    radialOptionLabel: i18next.t('Radial'),
    flipActionLabel: i18next.t('Flip order'),
    deleteActionLabel: i18next.t('Delete'),
  },
});

const AddColorSchemeDialogContainer = ({
  triggerId,
  elementIds = null,
  surfaceIndex = 0,
  surfaceAttribute = SURFACES_ATTRIBUTE,
  getSharedData,
  resolveSharedData,
  onDialogCancel,
}) => {
  const sharedDataSnapshot = useSharedDataSnapshot(getSharedData);

  const {
    offsetX: sharedOffsetX,
    offsetY: sharedOffsetY,
    engine: sharedEngine,
    originPalette: sharedOriginPalette,
    refId: sharedRefId,
    styleElement: sharedStyleElement,
    isDirty: sharedIsDirty,
    attrsSnapshot: sharedAttrsSnapshot,
    colorType: sharedColorType,
    schemeId: sharedSchemeId,
    originSurfacesById: sharedOriginSurfacesById,
  } = sharedDataSnapshot;

  const dispatch = useDispatch();
  const previewFrameRef = useRef(null);
  const latestPreviewRef = useRef(null);
  const styleElementRef = useRef(sharedStyleElement || null);
  const isDirty = useRef(sharedIsDirty || false);
  const attrsSnapshotRef = useRef(sharedAttrsSnapshot || null);
  const colorTypeRef = useRef(sharedColorType || null);
  const schemeIdRef = useRef(sharedSchemeId || null);

  const hasTargetElements = Boolean(sharedRefId || elementIds);
  const livePreviewEnabled = hasTargetElements && !bowser.mobile;

  const customColorPalettes = useSelector(customization.selectors.getCustomColorPalettes);
  const customColors = useSelector(customization.selectors.getCustomColors);

  const [originPalette] = useState(
    () => sharedOriginPalette || customization.accessors.getCurrentColorPalette()
  );

  const [engine] = useState(() => sharedEngine || createPaletteEngine(originPalette));

  const [targetElementIds] = useState(() => {
    if (elementIds) return elementIds;
    if (sharedRefId) return [sharedRefId];
    return [];
  });

  const [originSurfacesById] = useState(
    () =>
      sharedOriginSurfacesById ||
      targetElementIds.reduce((acc, elementId) => {
        acc[elementId] = getElementSurfaces(
          view.accessors.getLiveElement(elementId),
          surfaceAttribute
        );
        return acc;
      }, {})
  );

  const [{ originColors, initialColor, initialGradientCSSValue }] = useState(() => {
    const { colors, schemes } = engine.exportState();
    const resolvedInitialGradientCSSValue = getInitialGradientCSSValue({
      targetElementIds,
      originSurfacesById,
      surfaceIndex,
      schemes,
    });

    const resolvedInitialColor = resolvedInitialGradientCSSValue
      ? buildSolidColorFromGradientCSSValue(resolvedInitialGradientCSSValue) ||
        getInitialColor({ targetElementIds, originSurfacesById, surfaceIndex, schemes, colors })
      : getInitialColor({ targetElementIds, originSurfacesById, surfaceIndex, schemes, colors });

    return {
      originColors: DEFAULT_COLOR_SCHEMES.map((color) => ({
        value: colors[color],
        opacity: constants.MAX_OPACITY_VALUE,
      })),
      initialColor: resolvedInitialColor,
      initialGradientCSSValue: resolvedInitialGradientCSSValue,
    };
  });

  const {
    colorMode,
    solidColorHSLA,
    gradientColorModel,
    updateSolidColorHSLA,
    updateGradientColorModel,
    gradientBaseColorHSLA,
    getGradientBaseColorType,
    setGradientBaseColorTypeToAuto,
    handleColorModeChange,
    buildSharedDataPatch,
    removeInternalSharedData,
  } = useColorModeState({
    sharedData: sharedDataSnapshot,
    initialColor,
    opacity: 1,
    gradientCSSValue: initialGradientCSSValue,
  });
  const { predefinedGradients, saveCustomGradient } = usePredefinedGradients();
  const [gradientPickerKey, setGradientPickerKey] = useState(0);

  const { modalOffset, onModalDragEnd } = useDialogPosition({
    initialOffsetX: sharedOffsetX,
    initialOffsetY: sharedOffsetY,
  });

  const predefinedColors = useMemo(() => {
    const seen = new Set();
    const result = [];

    [...originColors, ...customColors].forEach((color) => {
      if (color.opacity !== constants.MAX_OPACITY_VALUE) return;

      const key = `${color.value}|${color.opacity}`;
      if (seen.has(key)) return;

      seen.add(key);
      result.push(color);
    });

    return result;
  }, [customColors, originColors]);

  const isOnlineStore = useMemo(
    () => sharedRefId && isOnlineStoreBlock(view.accessors.getLiveElement(sharedRefId)),
    [sharedRefId]
  );

  const gradientBaseColorCSSValue = useMemo(
    () =>
      gradientBaseColorHSLA
        ? new HSLColor(...Object.values(gradientBaseColorHSLA)).toCSSValue()
        : null,
    [gradientBaseColorHSLA]
  );

  const compilePalette = (
    { nextSolidColorHSLA, nextGradientModel = null },
    { generateLabel = false, forceNewSchemeId = false } = {}
  ) => {
    const baseColor = new HSLColor(
      nextSolidColorHSLA.h,
      nextSolidColorHSLA.s,
      nextSolidColorHSLA.l
    );
    const newColorType = isLightColorType(baseColor) ? LIGHT_COLOR_TYPE : DARK_COLOR_TYPE;

    if (colorTypeRef.current !== newColorType || forceNewSchemeId) {
      if (schemeIdRef.current) {
        engine.deleteCustomScheme(schemeIdRef.current);
      }
      colorTypeRef.current = newColorType;
      schemeIdRef.current = generateCustomSchemeId(newColorType);
      styleElementRef.current?.remove();
      styleElementRef.current = null;
    }

    let compiledPalette = engine.setCustomScheme(schemeIdRef.current, {
      baseColor: baseColor.toCSSValue(false),
      ...(generateLabel && { label: getCustomSchemeLabel(baseColor) }),
    });

    if (nextGradientModel) {
      compiledPalette = engine.updateSchemeOverrides(
        schemeIdRef.current,
        buildBlockBackgroundLayerOverrides({
          variableName: variableNames.BLOCK_BACKGROUND,
          hsl: nextSolidColorHSLA,
          gradientModel: nextGradientModel,
        })
      );
    }

    return compiledPalette;
  };

  const captureElementAttrs = () => {
    if (attrsSnapshotRef.current) return;

    attrsSnapshotRef.current = targetElementIds.reduce((acc, elementId) => {
      acc[elementId] = getElementAttrs(elementId);
      return acc;
    }, {});
  };

  const restoreElementAttrs = () => {
    if (!attrsSnapshotRef.current) return;

    const restoreActions = [];

    Object.entries(attrsSnapshotRef.current).forEach(([elementId, snapshot]) => {
      const currentAttrs = getElementAttrs(elementId);

      Object.entries(snapshot).forEach(([attr, originalValue]) => {
        if (currentAttrs[attr] !== originalValue) {
          restoreActions.push(view.actions.setElementAttribute(elementId, attr, originalValue));
        }
      });

      Object.keys(currentAttrs).forEach((name) => {
        if (!(name in snapshot)) {
          restoreActions.push(view.actions.removeElementAttribute(elementId, name));
        }
      });
    });

    if (restoreActions.length) {
      dispatch(
        view.actions.bulkViewActions(restoreActions, {
          strategy: updateStrategies.UPDATE_LIVE_ONLY,
          skipDesignAutomation: true,
        })
      );
    }
  };

  const setSurfaceAttribute = () => {
    if (styleElementRef.current) return;

    // first time applying live preview
    const setAttributeActions = targetElementIds.map((elementId) => {
      const surfaces = [...originSurfacesById[elementId]];
      surfaces[surfaceIndex] = schemeIdRef.current;
      return view.actions.setElementAttribute(elementId, surfaceAttribute, surfaces.join(' '));
    });

    dispatch(
      view.actions.bulkViewActions(setAttributeActions, {
        strategy: updateStrategies.UPDATE_LIVE_ONLY,
      })
    );
  };

  const applyLivePreview = (compiledPalette) => {
    styleElementRef.current?.remove();

    const appendedScheme = appendCustomSchemeToPaletteElement({
      schemeId: schemeIdRef.current,
      variables: compiledPalette.variables.schemes[schemeIdRef.current],
      parentNode: view.accessors.getLiveDocument(),
    });

    if (!appendedScheme) return;

    captureElementAttrs();
    setSurfaceAttribute();
    dispatch(actions.changeColor(compiledPalette.variables));
    styleElementRef.current = appendedScheme.styleElement;
  };

  const scheduleLivePreview = (
    { nextSolidColorHSLA, nextGradientModel },
    { forceNewSchemeId = false } = {}
  ) => {
    if (!livePreviewEnabled) return;

    latestPreviewRef.current = { nextSolidColorHSLA, nextGradientModel };

    if (previewFrameRef.current) return;

    previewFrameRef.current = requestAnimationFrame(() => {
      previewFrameRef.current = null;
      const compiledPalette = compilePalette(latestPreviewRef.current, {
        forceNewSchemeId,
      });
      isDirty.current = true;
      applyLivePreview(compiledPalette);
    });
  };

  const cancelScheduledPreview = useCallback(() => {
    if (!livePreviewEnabled) return;

    if (previewFrameRef.current) {
      cancelAnimationFrame(previewFrameRef.current);
      previewFrameRef.current = null;
    }
  }, [livePreviewEnabled]);

  const applySolidColor = ({ hsl }, { stopScheduleLivePreview = false } = {}) => {
    updateSolidColorHSLA({ ...hsl });

    if (!stopScheduleLivePreview) {
      scheduleLivePreview({ nextSolidColorHSLA: hsl, nextGradientModel: null });
    }
  };

  const handleSolidColorChange = ({ hsl }) => {
    applySolidColor({ hsl }, { stopScheduleLivePreview: isOnlineStore });
  };

  const handleColorChangeComplete = ({ hsl }) => {
    scheduleLivePreview(
      { nextSolidColorHSLA: hsl, nextGradientModel: null },
      { forceNewSchemeId: true }
    );
  };

  const handlePredefinedColorClick = ({ hslString }) => {
    const { h, s, l } = parseHslColorString(hslString);
    const hsl = { h, s, l, a: 1 };

    applySolidColor({ hsl });
  };

  const applyGradientColorModel = (
    { nextGradientModel },
    { stopScheduleLivePreview = false } = {}
  ) => {
    const nextSolidColorHSLA =
      getGradientBaseColorType() === gradientBaseColorTypes.AUTO
        ? deriveGradientBaseColorFromGradientModel(nextGradientModel) || gradientBaseColorHSLA
        : gradientBaseColorHSLA;

    updateGradientColorModel(nextGradientModel);

    if (!stopScheduleLivePreview) {
      scheduleLivePreview({ nextSolidColorHSLA, nextGradientModel });
    }
  };

  const handleGradientChange = (newGradient) => {
    applyGradientColorModel(
      { nextGradientModel: newGradient.model },
      { stopScheduleLivePreview: isOnlineStore }
    );
  };

  const handleGradientChangeEnd = (change) => {
    applyGradientColorModel({ nextGradientModel: change.model });

    switch (change.reason) {
      case GRADIENT_CHANGE_REASONS.CHANGE_LINEAR_ANGLE: {
        trackEvent(events.DESIGN_SETTINGS_GRADIENT_ANGLE_CHANGED, {
          angle: Number(change.model.orientation.value),
        });
        break;
      }
      case GRADIENT_CHANGE_REASONS.CHANGE_RADIAL_POSITION:
      case GRADIENT_CHANGE_REASONS.CHANGE_STOP_POSITION: {
        trackEvent(events.DESIGN_SETTINGS_GRADIENT_POSITION_CHANGED);
        break;
      }
      case GRADIENT_CHANGE_REASONS.ADD_STOP:
      case GRADIENT_CHANGE_REASONS.FLIP_STOPS:
      case GRADIENT_CHANGE_REASONS.DELETE_STOP: {
        trackEvent(events.DESIGN_SETTINGS_GRADIENT_CONTROL_CLICKED, {
          gradientType: getGradientTraitTypeByGradientModelType(change.model?.type),
          controlType: change.reason,
          stopsCount: change.model?.colorStops?.length,
        });
        break;
      }
      default:
        break;
    }
  };

  const handlePredefinedGradientClick = ({ value }) => {
    const nextGradientModel = buildGradientModelByString(value);
    if (!nextGradientModel) return;

    setGradientBaseColorTypeToAuto();
    setGradientPickerKey((currentKey) => currentKey + 1);
    applyGradientColorModel({ nextGradientModel });
  };

  const handleGradientColorStopClick = ({ colorStopId, colorStop }) => {
    const [h, s, l, a] = colorStop.value.map(Number);
    const hslColor = new HSLColor(h, s, l, a);
    const nextSharedGradientModel = gradientColorModel
      ? {
          ...gradientColorModel,
          activeColorStopId: colorStopId,
          colorStops: gradientColorModel.colorStops.map((gradientColorStop) =>
            gradientColorStop.id === colorStopId ? colorStop : gradientColorStop
          ),
        }
      : null;

    applyGradientColorModel({ nextGradientModel: nextSharedGradientModel });
    resolveSharedData({
      offsetX: modalOffset.x,
      offsetY: modalOffset.y,
      engine,
      originPalette,
      refId: sharedRefId,
      schemeId: schemeIdRef.current,
      colorType: colorTypeRef.current,
      attrsSnapshot: attrsSnapshotRef.current,
      styleElement: styleElementRef.current,
      originSurfacesById,
      isDirty: isDirty.current,
      ...buildSharedDataPatch({ gradientColorModel: nextSharedGradientModel }),
    });

    const handleColorStopPickerChange = ({ hsl }) => {
      if (!nextSharedGradientModel) return;

      const nextGradientModel = updateActiveColorStopInGradientModel(nextSharedGradientModel, hsl);

      applyGradientColorModel({ nextGradientModel });
    };

    const handleColorStopPickerSubmit = ({
      color: hslaColor,
      offsetX: nextOffsetX,
      offsetY: nextOffsetY,
    }) => {
      const nextGradientModel = updateActiveColorStopInGradientModel(
        nextSharedGradientModel,
        hslaColor
      );

      resolveSharedData({
        ...buildSharedDataPatch({ gradientColorModel: nextGradientModel }),
        offsetX: nextOffsetX,
        offsetY: nextOffsetY,
      });
    };

    const handleColorStopPickerCancel = ({ offsetX: nextOffsetX, offsetY: nextOffsetY }) => {
      if (livePreviewEnabled && !isOnlineStore) {
        cancelScheduledPreview();

        const restoredPalette = compilePalette({
          nextSolidColorHSLA: gradientBaseColorHSLA,
          nextGradientModel: nextSharedGradientModel,
        });

        applyLivePreview(restoredPalette);
      }

      resolveSharedData({ offsetX: nextOffsetX, offsetY: nextOffsetY });
    };

    dispatch(
      dialogs.actions.show(dialogTypes.COLOR_PICKER_DIALOG, {
        color: hslColor.toCSSValue(),
        enableAlpha: true,
        callerDialogType: dialogTypes.ADD_COLOR_SCHEME_DIALOG,
        supportedModes: [supportedColorModes.SOLID],
        mode: supportedColorModes.SOLID,
        onColorChange: handleColorStopPickerChange,
        onDialogMainAction: handleColorStopPickerSubmit,
        onDialogCancel: handleColorStopPickerCancel,
        callerDialogProps: {
          triggerId,
          elementIds,
          surfaceIndex,
          surfaceAttribute,
        },
      })
    );
  };

  const handleGradientBaseColorClick = () => {
    resolveSharedData({
      offsetX: modalOffset.x,
      offsetY: modalOffset.y,
      engine,
      originPalette,
      refId: sharedRefId,
      schemeId: schemeIdRef.current,
      colorType: colorTypeRef.current,
      attrsSnapshot: attrsSnapshotRef.current,
      styleElement: styleElementRef.current,
      originSurfacesById,
      isDirty: isDirty.current,
      ...buildSharedDataPatch(),
    });

    const handleFallbackPickerChange = ({ hsl }) => {
      if (!gradientColorModel) return;

      scheduleLivePreview({
        nextSolidColorHSLA: hsl,
        nextGradientModel: gradientColorModel,
      });
    };

    const handleFallbackPickerSubmit = ({ color, offsetX: nextOffsetX, offsetY: nextOffsetY }) => {
      resolveSharedData({
        ...buildSharedDataPatch({ gradientCustomBaseColorHSLA: color }),
        offsetX: nextOffsetX,
        offsetY: nextOffsetY,
      });
    };

    const handleFallbackPickerCancel = ({ offsetX: nextOffsetX, offsetY: nextOffsetY }) => {
      if (livePreviewEnabled && !isOnlineStore && gradientColorModel) {
        cancelScheduledPreview();

        const restoredPalette = compilePalette({
          nextSolidColorHSLA: gradientBaseColorHSLA,
          nextGradientModel: gradientColorModel,
        });

        applyLivePreview(restoredPalette);
      }

      resolveSharedData({ offsetX: nextOffsetX, offsetY: nextOffsetY });
    };

    dispatch(
      dialogs.actions.show(dialogTypes.COLOR_PICKER_DIALOG, {
        color: gradientBaseColorCSSValue,
        enableAlpha: false,
        callerDialogType: dialogTypes.ADD_COLOR_SCHEME_DIALOG,
        supportedModes: [supportedColorModes.SOLID],
        mode: supportedColorModes.SOLID,
        onColorChange: handleFallbackPickerChange,
        onDialogMainAction: handleFallbackPickerSubmit,
        onDialogCancel: handleFallbackPickerCancel,
        callerDialogProps: {
          triggerId,
          elementIds,
          surfaceIndex,
          surfaceAttribute,
        },
      })
    );
  };

  const handleModeChange = (nextMode) => {
    if (nextMode === colorMode) {
      return;
    }

    const nextState = handleColorModeChange(nextMode);
    scheduleLivePreview(nextState);
    trackEvent(events.DESIGN_SETTINGS_COLOR_TYPE_SWITCHED, { colorType: nextMode });
  };

  const updateSharedData = (customData = {}) => {
    resolveSharedData((current) => ({
      ...removeInternalSharedData(current),
      offsetX: modalOffset.x,
      offsetY: modalOffset.y,
      ...customData,
    }));
  };

  const handleCancel = () => {
    cancelScheduledPreview();

    if (livePreviewEnabled && isDirty.current) {
      styleElementRef.current?.remove();
      styleElementRef.current = null;

      engine.deleteCustomScheme(schemeIdRef.current);
      restoreElementAttrs();
      dispatch(actions.changeColor(engine.getOutput().variables));
    }

    onDialogCancel({ triggerId });
    updateSharedData();

    if (triggerId === triggerIds.BLOCK_SETTINGS) {
      dispatch(
        dialogs.actions.show(dialogTypes.BLOCK_SETTINGS, {
          tabId: BLOCK_SETTINGS_APPEARANCE_TAB_ID,
        })
      );
      return;
    }

    if (triggerId === triggerIds.ELEMENT_SETTINGS) {
      dispatch(dialogs.actions.show(dialogTypes.ELEMENT_SETTINGS_DIALOG));
      return;
    }

    dispatch(
      dialogs.actions.show(dialogTypes.DESIGN_SETTINGS_CUSTOM_PALETTE, {
        triggerId,
        colorPreset: originPalette.id,
      })
    );
  };

  const handleSubmit = () => {
    cancelScheduledPreview();

    const nextGradientModel =
      colorMode === supportedColorModes.GRADIENT ? gradientColorModel : null;
    let nextSolidColorHSLA = solidColorHSLA;

    if (colorMode === supportedColorModes.GRADIENT && nextGradientModel) {
      nextSolidColorHSLA = gradientBaseColorHSLA;
    }

    const compiledPalette = compilePalette(
      { nextSolidColorHSLA, nextGradientModel },
      { generateLabel: true }
    );
    styleElementRef.current?.remove();
    appendCustomSchemeToPaletteElement({
      schemeId: schemeIdRef.current,
      variables: compiledPalette.variables.schemes[schemeIdRef.current],
      parentNode: view.accessors.getLiveDocument(),
    });

    if (hasTargetElements) {
      setSurfaceAttribute();
      dispatch(actions.changeColor(compiledPalette.variables));
    }

    let additionalTraits;

    if (colorMode === supportedColorModes.SOLID) {
      const hslColorString = new HSLColor(...Object.values(solidColorHSLA)).toCSSValue(false);
      const isSelectedColorPredefined = predefinedColors.some(
        ({ value, opacity }) => value === hslColorString && opacity === solidColorHSLA.a
      );

      if (!isSelectedColorPredefined) {
        dispatch(
          customization.actions.addCustomColor({
            value: hslColorString,
            opacity: solidColorHSLA.a,
          })
        );
      }

      additionalTraits = {
        colorType: supportedColorModes.SOLID,
        gradientType: 'none',
        baseColor: 'none',
      };
    }

    if (colorMode === supportedColorModes.GRADIENT) {
      const gradientCSSValue = stringifyGradientModel(gradientColorModel) || null;
      saveCustomGradient(gradientCSSValue);

      additionalTraits = {
        colorType: supportedColorModes.GRADIENT,
        gradientType: gradientColorModel
          ? getGradientTraitTypeByGradientModelType(gradientColorModel.type)
          : 'none',
        baseColor: getGradientBaseColorType(),
      };
    }

    const paletteState = engine.exportState();
    const updatedPalettes = updatePalettesWith(paletteState, customColorPalettes);
    updateCustomPalettes(customColorPalettes, updatedPalettes);
    saveGlobalPalette({ compiledPalette });

    trackEvent(events.ADD_COLOR_SCHEME_DIALOG_SUBMITTED, { triggerId, ...additionalTraits });
    updateSharedData({ engine, originPalette });

    dispatch(
      dialogs.actions.show(dialogTypes.ADVANCED_COLORS_SETTINGS_DIALOG, {
        colorSchemeId: schemeIdRef.current,
        triggerId,
      })
    );
  };

  useEffect(() => {
    trackEvent(events.ADD_COLOR_SCHEME_DIALOG_DISPLAYED, {
      triggerId,
      colorType: colorMode,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const captions = getCaptions();

  return (
    <ColorSettingsDialog
      captions={captions}
      offsetX={modalOffset.x}
      offsetY={modalOffset.y}
      supportedModes={[supportedColorModes.SOLID, supportedColorModes.GRADIENT]}
      mode={colorMode}
      color={solidColorHSLA}
      predefinedColors={predefinedColors}
      predefinedGradients={predefinedGradients}
      gradientModel={gradientColorModel}
      gradientPickerKey={gradientPickerKey}
      gradientBaseColor={gradientBaseColorCSSValue}
      onColorChange={handleSolidColorChange}
      onColorChangeComplete={handleColorChangeComplete}
      onSubmit={handleSubmit}
      onCancel={handleCancel}
      onDragEnd={onModalDragEnd}
      onPredefinedColorClick={handlePredefinedColorClick}
      onModeChange={handleModeChange}
      onGradientChange={handleGradientChange}
      onGradientChangeEnd={handleGradientChangeEnd}
      onGradientColorStopClick={handleGradientColorStopClick}
      onPredefinedGradientClick={handlePredefinedGradientClick}
      onGradientBaseColorClick={handleGradientBaseColorClick}
    />
  );
};

AddColorSchemeDialogContainer.propTypes = {
  triggerId: PropTypes.oneOf([
    triggerIds.BLOCK_SETTINGS,
    triggerIds.ELEMENT_SETTINGS,
    triggerIds.COLOR_PALETTE_SETTINGS,
  ]).isRequired,
  elementIds: PropTypes.arrayOf(PropTypes.string),
  surfaceIndex: PropTypes.number,
  surfaceAttribute: PropTypes.string,
  getSharedData: PropTypes.func.isRequired,
  resolveSharedData: PropTypes.func.isRequired,
  onDialogCancel: PropTypes.func.isRequired,
};

export default AddColorSchemeDialogContainer;
